import { sql } from "drizzle-orm"
import {
  type AnyPgColumn,
  boolean,
  check,
  decimal,
  index,
  pgEnum,
  pgTable,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
  varchar,
} from "drizzle-orm/pg-core"
import { liabilities } from "./asset"
import { contacts, locations } from "./contact"
import { documents } from "./document"
import { estates } from "./estate"
import { trusts } from "./trust"

// Enums

export const companyOwnershipEnum = pgEnum("company_ownership_enum", [
  "DONT_OWN_A_COMPANY",
  "WHOLLY_OWNED_COMPANY",
  "SHARES_IN_PRIVATE_COMPANY",
])

export const companyContactRoleEnum = pgEnum("company_contact_role_enum", [
  "OWNER",
  "PARTNER",
  "SHAREHOLDER",
  "DIRECTOR",
  "OFFICER",
  "EMPLOYEE",
  "ADVISOR",
  "CLIENT",
  "OTHER",
])

export const shareTypeEnum = pgEnum("share_type_enum", ["COMMON", "PREFERRED", "OTHER"])

export const companyTypeEnum = pgEnum("company_type_enum", [
  "Accounting Firm",
  "Bank",
  "Charity",
  "Corporate Liquidator",
  "Corporation",
  "CPA Firm",
  "Credit Card",
  "Credit Union",
  "Financial Advisory",
  "Financial Institution",
  "Financial Services",
  "Financial Services Company",
  "Government Entity",
  "Healthcare",
  "Insurance",
  "Investment Firm",
  "Labor Union",
  "Law Firm",
  "Life Insurance Company",
  "Limited Liability Company",
  "Maintenance",
  "Marina",
  "Mortgage",
  "Notary",
  "Other",
  "Property Management",
  "Trust",
  "Trust Company",
  "Utilities",
  "Vehicle Loan",
])

// Core Tables

// Purpose: Stores general information about organizations, organizations group professional users
export const organizations = pgTable("organization", {
  id: uuid("id").primaryKey().defaultRandom(),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "set null" }),
  name: varchar("name", { length: 255 }).notNull().unique(),
  description: text("description"),
  website: varchar("website", { length: 255 }),
  // Pull url from a cloud storage bucket
  logoURL: varchar("logo_url", { length: 255 }),
  phone: varchar("phone", { length: 50 }),
  email: varchar("email", { length: 255 }),
  userId: uuid("user_id")
    .notNull()
    .references(() => contacts.id, { onDelete: "cascade" }),
  locationId: uuid("location_id").references(() => locations.id, { onDelete: "set null" }),
  adminId: uuid("admin_id")
    .notNull()
    .references(() => contacts.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required. This schema sets the timestamp on creation,
  // and application logic or a trigger should handle updates.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Stores specific details about companies. Companies can be associated with a journal, a user or a contact
export const companies = pgTable(
  "company",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    estateId: uuid("estate_id")
      .notNull()
      .references(() => estates.id, { onDelete: "cascade" }),
    companyType: companyTypeEnum("company_type").notNull().default("Other"),
    locationId: uuid("location_id").references(() => locations.id, { onDelete: "set null" }),
    companyDetails: text("company_details"),
    companyName: varchar("company_name", { length: 255 }).notNull(),
    companyOwnership: companyOwnershipEnum("company_ownership")
      .default("WHOLLY_OWNED_COMPANY")
      .notNull(),
    taxIdentificationNumber: varchar("tax_identification_number", { length: 50 }),
    employerIdentificationNumber: varchar("employer_identification_number", { length: 50 }),
    liabilityId: uuid("liability_id").references(() => liabilities.id, { onDelete: "set null" }),
    isDraft: boolean("is_draft").default(false).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
    // a database trigger is required. This schema sets the timestamp on creation,
    // and application logic or a trigger should handle updates.
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [uniqueIndex("company_estate_name_uq").on(table.estateId, table.companyName)],
)

// Junction Tables

// Purpose: Links companies to contacts with role and ownership information
export const companyContacts = pgTable(
  "company_contact",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    companyId: uuid("company_id")
      .notNull()
      .references(() => companies.id, { onDelete: "cascade" }),
    contactId: uuid("contact_id")
      .notNull()
      .references(() => contacts.id, { onDelete: "cascade" }),
    role: companyContactRoleEnum("role").notNull().default("OTHER"),
    ownershipPercentage: decimal("ownership_percentage", { precision: 5, scale: 2 }),
    shareType: shareTypeEnum("share_type"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    unique("company_contact_company_contact_share_type_uq")
      .on(table.companyId, table.contactId, table.shareType)
      .nullsNotDistinct(),
  ],
)

// Purpose: Links companies to documents
export const companyDocuments = pgTable("company_document", {
  id: uuid("id").primaryKey().defaultRandom(),
  companyId: uuid("company_id")
    .notNull()
    .references(() => companies.id, { onDelete: "cascade" }),
  documentId: uuid("document_id")
    .notNull()
    .references(() => documents.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Junction table for company owners — supports contact, trust, or company as owner (one per row)
export const companyOwners = pgTable(
  "company_owner",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    companyId: uuid("company_id")
      .notNull()
      .references(() => companies.id, { onDelete: "cascade" }),
    contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }),
    companyOwnerId: uuid("company_owner_id").references((): AnyPgColumn => companies.id, {
      onDelete: "cascade",
    }),
    trustId: uuid("trust_id").references(() => trusts.id, { onDelete: "cascade" }),
    ownershipPercentage: decimal("ownership_percentage", { precision: 5, scale: 2 })
      .default("100")
      .notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    check(
      "company_owner_one_owner_chk",
      sql`(
        (CASE WHEN ${table.contactId} IS NOT NULL THEN 1 ELSE 0 END) +
        (CASE WHEN ${table.companyOwnerId} IS NOT NULL THEN 1 ELSE 0 END) +
        (CASE WHEN ${table.trustId} IS NOT NULL THEN 1 ELSE 0 END)
      ) = 1`,
    ),
    index("co_company_id_idx").on(table.companyId),
    index("co_contact_id_idx").on(table.contactId),
    index("co_company_owner_id_idx").on(table.companyOwnerId),
    index("co_trust_id_idx").on(table.trustId),
    uniqueIndex("company_owner_contact_uq")
      .on(table.companyId, table.contactId)
      .where(sql`${table.contactId} IS NOT NULL`),
    uniqueIndex("company_owner_company_owner_uq")
      .on(table.companyId, table.companyOwnerId)
      .where(sql`${table.companyOwnerId} IS NOT NULL`),
    uniqueIndex("company_owner_trust_uq")
      .on(table.companyId, table.trustId)
      .where(sql`${table.trustId} IS NOT NULL`),
  ],
)
